import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FlaskConical } from "lucide-react";
import Logo from "./Logo";

const DISCLAIMER_TEXT = `The information presented on this website — including all educational material, diagrams and product descriptions — is provided for general educational and research-context purposes only. Nothing on this site is medical advice, and nothing here should be understood to recommend, promote or prescribe any therapy, treatment or protocol. Exoplus Bio products are cosmetics, intended to support the appearance of skin and hair only; they are not approved to diagnose, treat, cure or prevent any disease. Individual results vary. As always, treatment decisions should be made between doctor and patient.`;

export function DisclaimerGate() {
  const [accepted, setAccepted] = useState(
    () => sessionStorage.getItem("exoplusbio-disclaimer") === "1"
  );
  const [open, setOpen] = useState(false);
  const filmClosed = useRef(false);

  useEffect(() => {
    if (accepted) return;
    const path = window.location.pathname;
    if (path.startsWith("/admin") || path.startsWith("/crm")) return;

    const show = () => {
      if (!sessionStorage.getItem("exoplusbio-disclaimer")) setOpen(true);
    };
    const onFilmClosed = () => {
      filmClosed.current = true;
      show();
    };
    window.addEventListener("exoplusbio:film-closed", onFilmClosed);
    const t = setTimeout(() => {
      if (!filmClosed.current && sessionStorage.getItem("exoplusbio-film-shown") !== "1") show();
    }, 3500);
    return () => {
      window.removeEventListener("exoplusbio:film-closed", onFilmClosed);
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accepted]);

  if (accepted || !open) return null;

  const accept = () => {
    sessionStorage.setItem("exoplusbio-disclaimer", "1");
    setAccepted(true);
    setOpen(false);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#070b0c]/95 px-5 backdrop-blur-sm"
      data-testid="disclaimer-gate"
      role="dialog"
      aria-modal="true"
      aria-label="Disclaimer"
    >
      <div className="w-full max-w-lg border border-white/15 bg-[#0a1112] p-8 shadow-2xl md:p-10">
        <Logo dark />
        <div className="mt-7 inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#0e8c8c]/50 bg-[#0e8c8c]/10">
          <FlaskConical className="h-5 w-5 text-[#0e8c8c]" />
        </div>
        <h2 className="font-display mt-5 text-2xl leading-snug text-white md:text-3xl">
          Disclaimer — research purpose only
        </h2>
        <p className="mt-5 text-[13px] leading-relaxed text-white/70" data-testid="disclaimer-gate-text">
          {DISCLAIMER_TEXT}
        </p>
        <button
          onClick={accept}
          data-testid="disclaimer-accept-button"
          className="font-lab mt-8 w-full bg-[#0e8c8c] px-6 py-4 text-[11px] uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-white hover:text-[#070b0c]"
        >
          I understand — enter site
        </button>
        <p className="font-lab mt-5 text-[9px] uppercase tracking-[0.14em] text-white/40">
          Full details on our Education &amp; Compliance pages
        </p>
      </div>
    </div>,
    document.body
  );
}

export default DisclaimerGate;